import { useRef, useState, useEffect, cloneElement } from "react";
import {
  motion, useScroll, useTransform, useSpring, useMotionValue, useInView,
} from "framer-motion";

// --- desktop detection (pinned scroll effects are desktop-only for smooth mobile) ---
export function useIsDesktop() {
  const [d, setD] = useState(true);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 768px)");
    const on = () => setD(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
}

// --- line/word reveal heading (Wix-style clipped text rise) ---
// Bulletproof: text is ALWAYS revealed even if the in-view trigger doesn't fire
// (a mount fallback flips it visible after a short delay), so headings can never
// get stuck hidden in a production build.
export function RevealText({ text, className = "", as = "h2", delay = 0 }) {
  const Tag = motion[as] || motion.h2;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 700);
    return () => clearTimeout(t);
  }, []);
  const show = inView || fallback;
  const words = String(text).split(" ");
  return (
    <Tag ref={ref} className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom leading-[1.12] pt-[0.08em] pb-[0.22em] -mb-[0.22em]">
          <motion.span
            className="inline-block leading-[1.12]"
            initial={{ y: "115%" }}
            animate={{ y: show ? 0 : "115%" }}
            transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1], delay: show ? delay + i * 0.045 : 0 }}
          >
            {w}&nbsp;
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// --- scroll-linked zoom/parallax reveal for images (gap-safe: scale stays >= 1.08) ---
export function ScrollZoom({ children, className = "", from = 1.22 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [from, 1.08]);
  const y = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ scale, y }} className="w-full h-full will-change-transform">
        {children}
      </motion.div>
    </div>
  );
}

// --- auto-drifting image marquee (keeps "in motion" feel, never traps scroll) ---
export function HorizontalGallery({ children }) {
  const items = Array.isArray(children) ? children : [children];
  const loop = [...items, ...items].map((c, i) => cloneElement(c, { key: i }));
  return (
    <div className="overflow-hidden marquee-mask py-2">
      <div className="flex w-max gap-8 pl-8 animate-marquee-slow hover:[animation-play-state:paused]">
        {loop}
      </div>
    </div>
  );
}

// --- auto-advancing steps (text + crossfading visual), normal-height, scroll-friendly ---
export function StickySteps({ steps, eyebrow, heading }) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 3400);
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <section className="py-24 bg-[#1A1A18] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14 items-center">
        <div>
          {eyebrow && <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">{eyebrow}</p>}
          {heading && <h2 className="font-heading font-bold tracking-tight text-3xl md:text-5xl leading-tight mb-10">{heading}</h2>}
          <div className="space-y-3">
            {steps.map((s, i) => {
              const on = i === active;
              return (
                <button
                  key={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  data-cursor
                  className={`w-full text-left flex gap-5 rounded-xl p-4 transition-colors duration-300 ${on ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}
                >
                  <div className={`font-heading font-black text-2xl transition-colors duration-300 ${on ? "text-[#C84B31]" : "text-white/25"}`}>
                    0{i + 1}
                  </div>
                  <div>
                    <h3 className={`font-heading font-semibold text-xl transition-colors ${on ? "text-white" : "text-white/75"}`}>{s.title}</h3>
                    <p className={`text-sm mt-1 max-w-sm transition-opacity duration-300 ${on ? "text-white/70 opacity-100" : "text-white/55"}`}>{s.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="relative h-[58vh] min-h-[360px] rounded-2xl overflow-hidden">
          {steps.map((s, i) => (
            <motion.img
              key={i} src={s.image} alt={s.title}
              className="absolute inset-0 w-full h-full object-cover"
              animate={{ opacity: i === active ? 1 : 0, scale: i === active ? 1 : 1.08 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/50 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex gap-2">
            {steps.map((_, i) => (
              <div key={i} className="h-1 flex-1 rounded-full bg-white/20 overflow-hidden">
                <div className={`h-full bg-[#C84B31] transition-all duration-500 ${i <= active ? "w-full" : "w-0"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// --- custom blended cursor (desktop only) ---
export function CustomCursor() {
  const desktop = useIsDesktop();
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 500, damping: 40, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 500, damping: 40, mass: 0.4 });
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!desktop) return undefined;
    const move = (e) => { x.set(e.clientX); y.set(e.clientY); };
    const over = (e) => setHover(!!e.target.closest("a,button,[data-cursor]"));
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseover", over); };
  }, [desktop, x, y]);

  if (!desktop) return null;
  return (
    <motion.div style={{ x: sx, y: sy }} className="pointer-events-none fixed top-0 left-0 z-[100] mix-blend-difference">
      <motion.div
        animate={{ scale: hover ? 2.6 : 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
      />
    </motion.div>
  );
}

// --- magnetic wrapper (buttons pull toward the pointer) ---
export function Magnetic({ children, className = "", strength = 0.4 }) {
  const desktop = useIsDesktop();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 18 });
  const sy = useSpring(y, { stiffness: 300, damping: 18 });
  const onMove = (e) => {
    if (!desktop || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => { x.set(0); y.set(0); };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={reset} style={{ x: sx, y: sy }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
}
